import { type StoryData } from '@storyblok/react';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import {
  type TrackingSource,
  createGTMEvent,
  createSnowplow,
  trackingSource,
} from '@/lib';
import { useStoryState } from '@/providers';
import { getSlugFromPath } from '@/utils';

import { isValidEmail } from '../../../blocks/utils/formFieldValidation';
import { IdleState } from './IdleState';
import { StyledContainer, StyledWrapper } from './Newsletter.styled';
import type { INewsletterProps, ResultStatesType } from './Newsletter.types';
import type { ValidationType } from './Newsletter.types';
import { ResultState, ResultStates } from './ResultState';
import {
  type NewsletterFormData,
  subscribeNewsletterAPI,
} from './subscribeNewsletterAPI';

export const Newsletter = ({ labels, settings }: INewsletterProps) => {
  const [resultState, setResultState] = useState<ResultStatesType | null>();
  const [validation, setValidation] = useState<ValidationType>({});
  const [solution, setSolution] = useState<string>('');
  const [formData, setFormData] = useState<NewsletterFormData>();
  const { state: storyState } = useStoryState();
  const router = useRouter();
  const { asPath } = router;
  const { language = 'en' } = getSlugFromPath(asPath);

  const validateFields = (
    fieldType: string,
    value: boolean | undefined | string
  ) => {
    switch (fieldType) {
      case 'email':
        return typeof value === 'string' && !isValidEmail(value);
      case 'consentsDataProcessing':
        return !value;
    }
  };

  const onHandleSubmit = async (
    event: React.SyntheticEvent,
    formData: NewsletterFormData
  ) => {
    event.preventDefault();

    const data = {
      ...formData,
      nodeId: settings.nodeId,
      language,
    };

    const validation = Object.keys(formData).reduce((acc, key) => {
      const isValid = validateFields(
        key,
        formData[key as keyof NewsletterFormData]
      );

      return {
        ...acc,
        [key]: isValid,
      };
    }, {} as Record<keyof NewsletterFormData, boolean>);

    if (validation.email || validation.consentsDataProcessing) {
      setValidation(validation);
      return;
    }

    setResultState(ResultStates.processing);
    setFormData(data);
  };

  const onHandleButtonError = () => {
    setResultState(null);
  };

  const getSolutionHandler = (solution: string) => {
    setSolution(solution);
  };

  useEffect(() => {
    if (!solution || !formData) return;

    (async () => {
      try {
        const res = await subscribeNewsletterAPI(formData);
        const result = await res.json();
        const status = result.result.toLowerCase();
        setResultState(status);

        // on a successful response, send the lead event
        if (status === ResultStates.ok) {
          const snowplow = createSnowplow(
            storyState.story as StoryData,
            storyState.marketConfig
          );
          snowplow.trackLead(trackingSource.newsletter as TrackingSource, {
            email: formData.email,
          });

          const gtmEvent = createGTMEvent(
            storyState.story as StoryData,
            storyState.marketConfig
          );
          gtmEvent.pushNewsletter();
        }
      } catch (e) {
        setResultState(ResultStates.error);
        setValidation({});
      }
    })();
  }, [solution, formData, storyState.story, storyState.marketConfig]);

  return (
    <StyledContainer>
      <StyledWrapper
        forwardedAs="div"
        width={{ laptop: 'm', tabletLandscape: 's' }}
      >
        {resultState ? (
          <ResultState
            resultState={resultState}
            getSolutionHandler={getSolutionHandler}
            resultLabels={labels.resultLabels}
            onHandleButtonError={onHandleButtonError}
          />
        ) : (
          <IdleState
            onHandleSubmit={onHandleSubmit}
            idleLabels={labels.idleLabels}
            validation={validation}
          />
        )}
      </StyledWrapper>
    </StyledContainer>
  );
};
